/**
 * theme.ts — MUI theme factory and brand palette for the admin panel.
 *
 * Exposes the raw brand palettes (light/dark), helpers to read them
 * (`getColors`, `COLORS`, `CARD_SHADOW`), and the `createAppTheme` factory that
 * `main.tsx` calls to build a direction- and mode-aware MUI `Theme`.
 * See `createAppTheme` for the full set of component style overrides.
 */
import { createTheme, type Theme } from '@mui/material/styles';

// Latin/EN UI font stack (used in LTR).
const fontFamily = '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
// Arabic/RTL UI font stack (used when direction === 'rtl').
const fontFamilyAr = '"Cairo", "Noto Sans Arabic", "IBM Plex Sans Arabic", "Segoe UI", Tahoma, sans-serif';

/** Theme color scheme selector. */
export type ThemeMode = 'light' | 'dark';

/**
 * Dawri Madaris — design language
 *
 * Layout: navy sidebar + content surface; generous whitespace; card-based grids.
 * Shape: large radius on cards/dialogs (16–20px); pills for status/filters.
 * Type: Inter / Cairo; bold metrics, semibold titles, muted labels.
 * RTL: theme.direction + logical CSS (marginInline, paddingInline) on new UI.
 *
 * Both light and dark palettes share the same brand identity (navy + teal accent).
 * Dark mode uses slate surfaces so the navy still reads as "brand" not "background".
 */

/**
 * Raw light-mode brand palette. The canonical color source; `darkColors` must
 * mirror its keys, and the MUI palette/typography in `createAppTheme` are
 * derived from whichever of these two is active.
 */
const lightColors = {
  sidebarBg: '#1e2a78',
  sidebarActive: '#2d3a9e',
  sidebarText: '#c7cce8',
  sidebarTextActive: '#ffffff',
  primary: '#1e2a78',
  primaryLight: '#3d4aac',
  accent: '#26b0a1',
  accentLight: '#e6f7f5',
  warning: '#f59e0b',
  warningLight: '#fffbeb',
  error: '#ef4444',
  errorLight: '#fef2f2',
  info: '#3b82f6',
  infoLight: '#eff6ff',
  success: '#22c55e',
  successLight: '#ecfdf3',
  bgPage: '#f8f9fb',
  bgCard: '#ffffff',
  bgAppBar: '#ffffff',
  textPrimary: '#0f172a',
  textSecondary: '#64748b',
  border: '#e2e8f0',
  cardShadow: '0 4px 24px rgba(15, 23, 42, 0.06)',
  statGreen: '#26b0a1',
  statBlue: '#3461d1',
  statOrange: '#fdb528',
  statRed: '#e94d91',
  statPurple: '#b069e1',
  statTeal: '#00b5ad',
};

/**
 * Raw dark-mode brand palette. Typed as `typeof lightColors` so it is forced to
 * keep the exact same keys; only the values differ (slate surfaces, lighter
 * brand tones). Inline comments note the intent behind each group.
 */
const darkColors: typeof lightColors = {
  // Sidebar keeps navy identity even in dark mode (brand is consistent).
  sidebarBg: '#161e54',
  sidebarActive: '#243087',
  sidebarText: '#b5bbd9',
  sidebarTextActive: '#ffffff',
  // Brand primary leans lighter on dark so it pops on dark surfaces.
  primary: '#5b6dd6',
  primaryLight: '#7c8be3',
  accent: '#3dcebd',
  accentLight: '#0f3531',
  warning: '#fbbf24',
  warningLight: '#3f2e0d',
  error: '#f87171',
  errorLight: '#3b1414',
  info: '#60a5fa',
  infoLight: '#0f253f',
  success: '#4ade80',
  successLight: '#0e2e1a',
  // Page is the darkest slate; cards a step lighter; appbar one more step.
  bgPage: '#0b1220',
  bgCard: '#151d2e',
  bgAppBar: '#151d2e',
  textPrimary: '#f1f5f9',
  textSecondary: '#94a3b8',
  border: '#27314a',
  cardShadow: '0 4px 24px rgba(0, 0, 0, 0.4)',
  statGreen: '#3dcebd',
  statBlue: '#7187dd',
  statOrange: '#fbbf24',
  statRed: '#f472b6',
  statPurple: '#c084fc',
  statTeal: '#2dd4bf',
};

/**
 * Resolves to the active palette. Lets components that need raw values
 * (e.g. AdminLayout sidebar) read from a single source.
 *
 * @param mode `'dark'` returns `darkColors`, anything else returns `lightColors`.
 * @returns The raw color object for the requested mode.
 */
export function getColors(mode: ThemeMode): typeof lightColors {
  return mode === 'dark' ? darkColors : lightColors;
}

/**
 * Backwards-compat alias for components still importing `COLORS` directly.
 * Defaults to light. New components should call `getColors(mode)` from a hook
 * that reads the current mode instead.
 */
export const COLORS = lightColors;

/** Card elevation used across Paper/Card */
export const CARD_SHADOW = lightColors.cardShadow;

/**
 * Builds the MUI `Theme` for the admin panel from the active brand palette.
 *
 * Sets `direction`/`palette.mode`, maps brand colors onto MUI's
 * primary/secondary/status slots and background/text/divider, applies the
 * shared border radius and the direction-appropriate font family, and layers in
 * component style overrides (buttons, cards, paper, tables, chips, text fields,
 * dialogs) so the brand look is consistent without per-component styling.
 *
 * @param direction `'ltr'` or `'rtl'`; also selects the Latin vs Arabic font stack.
 * @param mode Light or dark scheme (default `'light'`).
 * @returns A ready-to-use MUI `Theme`.
 */
export function createAppTheme(direction: 'ltr' | 'rtl', mode: ThemeMode = 'light'): Theme {
  const c = getColors(mode);
  return createTheme({
    direction,
    palette: {
      mode,
      primary: { main: c.primary, light: c.primaryLight },
      secondary: { main: c.accent },
      success: { main: c.success },
      warning: { main: c.warning },
      error: { main: c.error },
      info: { main: c.info },
      background: { default: c.bgPage, paper: c.bgCard },
      text: { primary: c.textPrimary, secondary: c.textSecondary },
      divider: c.border,
    },
    shape: { borderRadius: 14 },
    typography: {
      fontFamily: direction === 'rtl' ? fontFamilyAr : fontFamily,
      h4: { fontWeight: 700, fontSize: '1.5rem' },
      h5: { fontWeight: 700, fontSize: '1.25rem' },
      h6: { fontWeight: 600, fontSize: '1.1rem' },
      subtitle1: { fontWeight: 600 },
      body2: { color: c.textSecondary },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { textTransform: 'none', borderRadius: 10, fontWeight: 600 },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: mode === 'dark'
                ? '0 2px 8px rgba(61, 206, 189, 0.45)'
                : '0 2px 8px rgba(38, 176, 161, 0.35)',
            },
          },
          containedSecondary: { color: mode === 'dark' ? c.textPrimary : '#fff' },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 20,
            boxShadow: c.cardShadow,
            border: `1px solid ${c.border}`,
            backgroundColor: c.bgCard,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: c.border, boxShadow: 'none' },
          elevation1: { boxShadow: c.cardShadow },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-head': {
              fontWeight: 600,
              fontSize: '0.75rem',
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              color: c.textSecondary,
              backgroundColor: c.bgPage,
              borderBottom: `1px solid ${c.border}`,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderColor: c.border, padding: '14px 16px' },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { '&:last-of-type td': { borderBottom: 0 } },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: 9999 },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: { '& .MuiOutlinedInput-root': { borderRadius: 10, backgroundColor: c.bgCard } },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 20,
            boxShadow: mode === 'dark'
              ? '0 24px 48px rgba(0, 0, 0, 0.6)'
              : '0 24px 48px rgba(15, 23, 42, 0.12)',
          },
        },
      },
    },
  });
}

/**
 * Default static theme (LTR + light). Convenience export for contexts without
 * live direction/mode (e.g. tests or standalone Storybook). The running app
 * uses the dynamic theme built in `main.tsx` via `createAppTheme`.
 */
export const theme = createAppTheme('ltr', 'light');
